import _ from 'lodash';
import { addDays, format } from 'date-fns';

import { getEdeMemberFullName } from '../utils/edeMember';
import { didMemberMoveFromForeignCountry, findMemberCoverage60DayBeforeCircumstanceChangeConfirmedIndicator } from './ede-member';

const getStatusFlags = status => ({
  open: status === 'ACTION_NEEDED',
  processing: status === 'PROCESSING',
  resolved: status === 'COMPLETE',
  expired: status === 'EXPIRED',
});

const immigrationStatus = {
  legacyId: 5,
  menu: [
    'PERMANENT_RESIDENT_GREEN_CARD',
    'REENTRY_PERMIT',
    'REFUGEE_TRAVEL_DOCUMENT',
    'EMPLOYMENT_AUTHORIZATION_CARD',
    'MACHINE_READABLE_IMMIGRANT_VISA',
    'TEMPORARY_I_551_STAMP_ON_PASSPORT_OR_I_94_I_94A',
    'ARRIVAL_DEPARTURE_RECORD',
    'ARRIVAL_DEPARTURE_RECORD_IN_FOREIGN_PASSPORT',
    'OTHER',
  ],
};

const dmiMap = {
  DMI_INCARCERATION: {
    legacyId: 6,
    menu: [
      'OFFICIAL_RELEASE_PAPERS',
      'DOCUMENT_SHOWING_EMPLOYMENT_PROOF_OF_RESIDENCE',
      'SIGNED_NOTARIZED_AFFIDAVIT',
    ],
  },
  DMI_TRIBE_MEMBERSHIP: {
    legacyId: 25,
    menu: [
      'TRIBAL_ENROLLMENT_MEMBERSHIP_CARD',
      'DOCUMENT_ISSUED_BY_BIA',
      'AUTHENTIC_DOCUMENT_FROM_A_TRIBE_DECLARING_MEMBERSHIP',
      'CERTIFICATE_OF_DEGREE_OF_INDIAN_BLOOD',
      'CERTIFICATE_OF_INDIAN_STATUS_CARD',
      'FORM_I_872_AMERICAN_INDIAN_CARD',
      'U_S_AMERICAN_INDIAN_ALASKA_NATIVE_TRIBAL_ENROLLMENT_OR_SHAREHOLDER_DOCUMENTATION',
      'LETTER_FROM_THE_MARKETPLACE_GRANTING_A_TRIBAL_EXEMPTION',
    ],
  },
  DMI_NONESCMEC: {
    legacyId: 29,
    menu: [],
  },
  DMI_ANNUAL_INCOME: {
    legacyId: 3,
    menu: [
      'FORM_1040_TAX_RETURN',
      'W2_AND_OR_1099S',
      'PAY_STUB_DOCUMENTATION',
      'SELF_EMPLOYMENT_DOCUMENTATION',
      'SOCIAL_SECURITY_STATEMENTS',
      'UNEMPLOYMENT_BENEFITS_LETTER',
      'OTHER',
    ],
  },
  DMI_CITIZENSHIP: {
    legacyId: 4,
    menu: [
      'U_S_PASSPORT',
      'CERTIFICATE_OF_NATURALIZATION',
      'CERTIFICATE_OF_CITIZENSHIP',
      'STATE_ISSUED_ENHANCED_DRIVER_S_LICENSE',
      'DOCUMENT_FROM_A_FEDERALLY_RECOGNIZED_INDIAN_TRIBE',
      'OTHER',
    ],
  },
  DMI_QHP_LAWFUL_PRESENCE: immigrationStatus,
  DMI_MEDICAID_LAWFUL_PRESENCE: immigrationStatus,
  DMI_ESCMEC: {
    legacyId: 7,
    menu: ['LETTER_FROM_HEALTH_INSURER', 'STATEMENT_OF_HEALTH_BENEFITS'],
  },
  DMI_SSN: {
    legacyId: 37,
    menu: ['SOCIAL_SECURITY_CARD', 'TAX_FORM_S', 'BENEFIT_OR_INCOME_STATEMENT'],
  },
};

const dmiNONESCMECMap: { [key: string]: DmiType } = {
  NON_ESC_MEC_MEDICAID_CHIP: {
    menu: ['LETTER_FROM_MEDICAID', 'LETTER_FROM_CHIP'],
  },
  NON_ESC_MEC_TRICARE: {
    menu: ['LETTER_FROM_TRICARE'],
  },
  NON_ESC_MEC_MEDICARE: {
    menu: ['LETTER_FROM_MEDICARE'],
  },
  NON_ESC_MEC_VA: {
    menu: ['LETTER_FROM_VA'],
  },
  NON_ESC_MEC_PEACE_CORPS: {
    menu: ['LETTER_FROM_PEACE_CORPS'],
  },
};

const sviMap = {
  '200_MOVED_TO_NEW_SERVICE_AREA': {
    legacyId: 19,
    menu: [
      'BILLS_FINANCIAL_STATEMENT_AT_NEW_ADDRESS',
      'U_S_POSTAL_SERVICE_CHANGE_OF_ADDRESS_LETTER',
      'MORTGAGE_RENTAL_DOCUMENT_AT_NEW_ADDRESS',
      'LETTER_FROM_A_GOVERNMENT_ORGANIZATION',
      'LETTER_FROM_AN_INSURANCE_COMPANY',
      'OTHER',
    ],
  },
  '209_MEC_LOSS': {
    legacyId: 17,
    menu: [
      'LETTER_FROM_AN_INSURANCE_COMPANY',
      'LETTER_FROM_AN_EMPLOYER',
      'LETTER_ABOUT_COBRA_COVERAGE',
      'HEALTH_CARE_PROGRAM_DOCUMENT',
      'PAY_STUBS',
      'LETTER_FOR_LOSS_OF_STUDENT_COVERAGE',
      'DOCUMENT_FOR_COVERAGE_LOSS_DUE_TO_DIVORCE_SEPARATION',
      'DOCUMENT_FOR_COVERAGE_LOSS_DUE_TO_DEATH',
      'LETTER_OF_EXPLANATION',
      'OTHER',
    ],
  },
  '203_MARRIAGE': {
    legacyId: 20,
    menu: [
      'MARRIAGE_CERTIFICATE',
      'MARRIAGE_LICENSE',
      'OFFICIAL_PUBLIC_RECORD',
      'AFFIDAVIT',
      'RELIGIOUS_DOCUMENT',
      'OTHER',
      'LETTER_OF_EXPLANATION',
    ],
  },
  '198_ADOPTION': {
    legacyId: 30,
    menu: [
      'ADOPTION_LETTER_OR_RECORD',
      'FOSTER_CARE_PAPERS',
      'COURT_ORDER',
      'LEGAL_GUARDIANSHIP_DOCUMENT',
      'MEDICAL_SUPPORT_ORDER',
      'DHS_IMMIGRATION_DOCUMENT',
      'OTHER',
      'LETTER_OF_EXPLANATION',
    ],
  },
  '472_SEP_GRANTED_FOR_MDCAID_DENIAL': {
    legacyId: 29,
    menu: [
      'DENIAL_LETTER_FROM_YOUR_STATE',
      'LETTER_STATING_LOST_COVERAGE',
      'MARKETPLACE_LETTER',
      'SCREENSHOT_OF_RESULTS',
      'OTHER',
      'LETTER_OF_EXPLANATION',
    ],
  },
};

const PREVIOUS_COVERAGE_DOCUMENTS = [
  'LETTER_OF_EXPLANATION',
  'LETTER_FROM_AN_INSURANCE_COMPANY',
  'HEALTH_CARE_PROGRAM_DOCUMENT',
  'DOCUMENT_FROM_AN_EMPLOYER',
];

const mapSVIs = (
  svi,
  priorCoverageConfirmationRequired = false,
  members,
): Array<Object> => {
  const record = sviMap[svi.verificationStatusReason];

  if (
    svi.verificationStatusReason === '203_MARRIAGE' &&
    priorCoverageConfirmationRequired
  ) {
    return [
      record,
      {
        id: '203_MARRIAGE_PRIOR_COVERAGE',
        menu: PREVIOUS_COVERAGE_DOCUMENTS,
      },
    ];
  }

  const sviMemberIds = svi.associatedMemberIdentifiers || [];
  const foreignMove = !!members.filter(
    m => sviMemberIds.includes(m.memberIdentifier),
  ).find(m => !!didMemberMoveFromForeignCountry(m));

  if (svi.verificationStatusReason === '200_MOVED_TO_NEW_SERVICE_AREA') {
    // Foreign move only required entrance info, regardless of prior coverage
    if (foreignMove) {
      return [
        {
          id: '200_MOVED_TO_NEW_SERVICE_AREA_PRIOR_FOREIGN_COVERAGE',
          menu: ['ARRIVAL_DEPARTURE_RECORD_I_94_I_94A', 'PASSPORT', 'OTHER'],
        },
      ];
    }

    const records = [record];
    if (priorCoverageConfirmationRequired) {
      records.push({
        id: '200_MOVED_TO_NEW_SERVICE_AREA_PRIOR_US_COVERAGE',
        menu: PREVIOUS_COVERAGE_DOCUMENTS,
      });
    }

    return records;
  }

  return [record];
};

const mapDmi = dmi =>
  (dmi.verificationType === 'DMI_NONESCMEC'
    ? Object.assign({}, dmiNONESCMECMap[dmi.dmiSubType], { id: dmi.dmiSubType })
    : dmiMap[dmi.verificationType]);

const translateKeySections = ['name', 'description', 'examples', 'supportLink'];

const makeTranslateKeys = (dmiOrSvi: Object) => translateKeySections.reduce(
  (result, key) => ({
    ...result,
    [key]: dmiOrSvi.legacyId
      ? `ede.tasks.${key}.${dmiOrSvi.legacyId}`
      : dmiOrSvi.id && `ede.tasks.edeDMISVIMap.${dmiOrSvi.id}.${key}`,
  }),
  {},
);

const mapDmiData = (dmiData = []) => {
  let dmiDocumentCount = 0;
  const mostRecentDmiData = dmiData.filter(({ applicationInformation }) => applicationInformation.isCurrentVersion);
  const mappedDmi = _.get(mostRecentDmiData, '[0].applicationInformation.personInformation', []).reduce((acc, person) => {
    const { firstName, lastName } = person;
    const mappedPersonDmi = _.get(person, 'dataMatchingIssue', []).map((dmi) => {
      const { dmiTimerEndDate, eventDateTime, verificationType, dmiStatus } = dmi;

      if (dmiStatus === 'NO_DMI') return null;

      if (_.get(dmi, 'dmiTimerStatusReason', '') === 'RESOLVED') return null;

      const dueDate = format(
        dmiTimerEndDate || addDays(eventDateTime, 90),
        'MM-DD-YYYY',
      );
      // checking DMI_NONESCMEC dmiSubType:
      const dmiSubType = _.get(dmi, 'dmiSubType');
      const dmiType =
        verificationType === 'DMI_NONESCMEC' && dmiSubType ? dmiSubType : verificationType;
      const statusFlags = getStatusFlags(dmi.consolidatedDmiStatus);

      if (statusFlags.open) {
        dmiDocumentCount += 1;
      }

      const dmiDocumentData = mapDmi(dmi);

      return {
        ...dmi,
        ...statusFlags,
        id: dmi.eventIdentifier,
        statusCode: dmi.consolidatedDmiStatus,
        dueDate,
        verificationType: dmiType,
        sep: false,
        requiredDocuments: [
          { index: 0, menu: dmiDocumentData.menu, translateKeys: makeTranslateKeys(dmiDocumentData) },
        ],
        type: 'DMI',
        edeParams: {
          issueType: 'DMI',
          personTrackingNumbers: [person.personTrackingNumber],
        },
        nameLabel: `${firstName} ${lastName}`,
      };
    }).filter(val => !!val);

    return acc.concat(mappedPersonDmi);
  }, []);

  return {
    dmiDocumentCount,
    mappedDmi,
  };
};

const getSviDueDate = (svi) => {
  const {
    endDate,
    sviCreationDateTime,
  } = svi;

  return format(
    endDate || addDays(sviCreationDateTime, 30),
    'MM-DD-YYYY',
  );
};

const mapSviData = (sviData = [], notices = [], members = []) => {
  let sviDocumentCount = 0;
  const mappedSvi= sviData.reduce((acc, application) => {
    const appSviData = application.sviInformation.map((svi) => {
      if (Array.isArray(svi.associatedMemberIdentifiers)) {
        const ffmId = svi.associatedMemberIdentifiers[0];
        const edeMember = members.find(({ memberIdentifier }) => memberIdentifier === ffmId);
        if (edeMember) {
          const name = getEdeMemberFullName(edeMember);
          let secondName = '';

          if (
            svi.verificationStatusReason === '203_MARRIAGE' &&
            svi.associatedMemberIdentifiers.length > 1
          ) {
            const secondFfmId = svi.associatedMemberIdentifiers[1];
            const secondMember = _.find(members, ['memberIdentifier', secondFfmId]);
            secondName = secondMember && getEdeMemberFullName(secondMember);
          }

          // Check for `false` explicitly - `true` is the same as null/undefined in this case
          const previousCoverageRequired =
            findMemberCoverage60DayBeforeCircumstanceChangeConfirmedIndicator(edeMember) === false;

          const statusFlags = getStatusFlags(svi.consolidatedSviStatus);
          const dueDate = getSviDueDate(svi);
          const sviDocuments = mapSVIs(svi, previousCoverageRequired, members);

          if (statusFlags.open) {
            sviDocumentCount += 1;
          }

          return {
            id: svi.sviIdentifier,
            name,
            nameLabel: `${name} ${secondName}`,
            verificationType: svi.verificationStatusReason,
            statusCode: svi.consolidatedSviStatus,
            secondName,
            type: 'SVI',
            dueDate,
            edeParams: {
              issueType: 'SVI',
              personTrackingNumbers: svi.associatedPersonTrackingNumbers,
            },
            requiredDocuments: sviDocuments.map((msg, index) => (
              { index, menu: msg.menu, translateKeys: makeTranslateKeys(msg) }
            )),
            sep: true,
            verificationStatusReason: svi.verificationStatusReason,
            documents: notices.filter(({ consumerAttestedVerificationIssueIdentifier }) => (
              +consumerAttestedVerificationIssueIdentifier === svi.sviIdentifier
            )),
            ...statusFlags,
          };
        }

        return null;
      }
    });

    return acc.concat(appSviData);
  }, []).filter(val => !!val);

  return {
    sviDocumentCount,
    mappedSvi,
  };
};

export const mapIssues = (
  issuesData: Object,
  members: Array<Object> = [],
) => {
  // ffmDmiStatus will be present as null, so defaulting to array in _.get does
  // not work
  const dmiData =
    _.get(issuesData, 'dmi.ffmDmiStatusResponse.ffmDmiStatus') || [];
  const sviData = _.get(
    issuesData,
    'svi.ffmSviInformationResponse.ffmSviInformation',
    [],
  );
  const notices = _.get(issuesData, 'notices', []);
  const { mappedDmi, dmiDocumentCount } = mapDmiData(dmiData);
  const { mappedSvi, sviDocumentCount } = mapSviData(sviData, notices, members);
  return {
    requiredDocumentsCount: dmiDocumentCount + sviDocumentCount,
    dmi: mappedDmi,
    svi: mappedSvi,
    issues: [...mappedSvi, ...mappedDmi],
  };
};

export const hasRequiredDocuments = (issue: EdeIssue): boolean =>
  !_.isEmpty(_.get(issue, 'requiredDocuments')) && !_.get(issue, 'resolved');
